import Component from '@glimmer/component';

interface ActionableCardSignature {
  Args: {
    title?: string;
    matchScore?: number;
    showBanner?: boolean;
    bannerText?: string;
    bannerClass?: string;
    cardClass?: string;
  };
  Blocks: {
    banner?: [];
    badge?: [];
    header?: [];
    metadata?: [];
    skills?: [];
    eligibility?: [];
    actions?: [];
    default?: [];
  };
  Element: HTMLDivElement;
}

/**
 * A flexible card component for displaying actionable items with optional
 * banner, match score, skill chips, eligibility badges, and action buttons.
 *
 * @example
 * ```gts
 * import ActionableCard from 'tio-ui/components/actionable-card';
 *
 * <ActionableCard @title="Data Science Certificate" @matchScore={{92}} @showBanner={{true}} @bannerText="Top Pick">
 *   <:skills>
 *     <SkillChip @name="Python" />
 *   </:skills>
 *   <:actions>
 *     <Button @intent="primary">Apply</Button>
 *   </:actions>
 * </ActionableCard>
 * ```
 */
export default class ActionableCard extends Component<ActionableCardSignature> {
  get bannerClass() {
    return this.args.bannerClass ?? 'tio-actionable-card__banner';
  }

  get cardClass() {
    return this.args.cardClass ?? 'tio-actionable-card';
  }

  get hasMatchScore() {
    return this.args.matchScore != null;
  }

  get scoreClass() {
    const score = this.args.matchScore ?? 0;
    if (score >= 90) return 'tio-actionable-card__score tio-actionable-card__score--high';
    if (score >= 70) return 'tio-actionable-card__score tio-actionable-card__score--mid';
    return 'tio-actionable-card__score tio-actionable-card__score--low';
  }

  <template>
    <div data-test-actionable-card class={{this.cardClass}} ...attributes>
      {{#if (has-block "banner")}}
        {{yield to="banner"}}
      {{else if @showBanner}}
        <div data-test-actionable-card-banner class={{this.bannerClass}}>
          <svg
            class="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M234.5,114.38l-45.1,39.36,13.51,58.6a16,16,0,0,1-23.84,17.34l-51.11-31-51,31a16,16,0,0,1-23.84-17.34L66.61,153.8,21.5,114.38a16,16,0,0,1,9.11-28.06l59.46-5.15,23.21-55.36a15.95,15.95,0,0,1,29.44,0h0L166,81.17l59.44,5.15a16,16,0,0,1,9.11,28.06Z"
            ></path>
          </svg>
          {{@bannerText}}
        </div>
      {{/if}}

      <div class="p-4">
        {{#if (has-block "header")}}
          {{yield to="header"}}
          {{#if (has-block "metadata")}}
            {{yield to="metadata"}}
          {{/if}}
        {{else if @title}}
          <div class="tio-row-start-between gap-4 mb-4">
            <div>
              <div class="tio-row-tight mb-2">
                <h4 data-test-actionable-card-title class="tio-text-lg-semibold">{{@title}}</h4>
                {{#if (has-block "badge")}}
                  {{yield to="badge"}}
                {{/if}}
              </div>
              {{#if (has-block "metadata")}}
                {{yield to="metadata"}}
              {{/if}}
            </div>
            {{#if this.hasMatchScore}}
              <div data-test-actionable-card-score class="text-center">
                <div class={{this.scoreClass}}>
                  {{@matchScore}}%
                </div>
                <span class="text-xs tio-fg-muted mt-1">Match</span>
              </div>
            {{/if}}
          </div>
        {{/if}}

        {{#if (has-block "skills")}}
          <div class="tio-wrap-tight mb-4">
            {{yield to="skills"}}
          </div>
        {{/if}}

        {{#if (has-block "eligibility")}}
          <div class="mb-4">
            {{yield to="eligibility"}}
          </div>
        {{/if}}

        {{#if (has-block "actions")}}
          <div class="tio-row-between tio-actionable-card__actions pt-4">
            {{yield to="actions"}}
          </div>
        {{/if}}

        {{#if (has-block)}}
          {{yield}}
        {{/if}}
      </div>
    </div>
  </template>
}
